import { useMemo, useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/auth/hooks';
import { useBoolean } from '@/hooks/state/use-boolean';

interface UseListLayoutProps<T> {
  entityName: string;
  defaultFilters: T;
  defaultRowsPerPage?: number;
}

interface UseListLayoutReturn<T> {
  filters: T;
  page: number;
  rowsPerPage: number;
  selected: string[];
  openFilters: ReturnType<typeof useBoolean>;

  // Setters
  setFilters: React.Dispatch<React.SetStateAction<T>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;

  // Handlers
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleFilters: (name: string, value: any) => void;
  handleFilterSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleResetFilters: () => void;
  handleSelect: (id: string) => void;
  handleSelectAll: (checked: boolean, items: string[]) => void;

  // Storage keys
  keyStorageFilters: string;
  keyStoragePage: string;
  keyStorageRow: string;
}

export function useListLayout<T extends Record<string, any>>({
  entityName,
  defaultFilters,
  defaultRowsPerPage = 100,
}: UseListLayoutProps<T>): UseListLayoutReturn<T> {
  const { user } = useAuthContext();

  // UI state
  const openFilters = useBoolean();

  // Memoize storage keys to avoid recalculation
  const { keyStorageFilters, keyStoragePage, keyStorageRow } = useMemo(
    () => ({
      keyStorageFilters: `${entityName}${user?._id}Filters`,
      keyStoragePage: `${entityName}${user?._id}Page`,
      keyStorageRow: `${entityName}${user?._id}Row`,
    }),
    [entityName, user?._id]
  );

  // Memoize initial state calculations
  const initialFilters = useMemo(() => {
    const storedFilters = sessionStorage.getItem(keyStorageFilters);
    return storedFilters ? { ...defaultFilters, ...JSON.parse(storedFilters) } : defaultFilters;
  }, [keyStorageFilters, defaultFilters]);

  const initialPage = useMemo(() => {
    const storedPage = sessionStorage.getItem(keyStoragePage);
    return storedPage ? +storedPage : 0;
  }, [keyStoragePage]);

  const initialRowsPerPage = useMemo(() => {
    const storedRowsPerPage = sessionStorage.getItem(keyStorageRow);
    return storedRowsPerPage ? +storedRowsPerPage : defaultRowsPerPage;
  }, [keyStorageRow, defaultRowsPerPage]);

  // Initialize state with memoized values
  const [filters, setFilters] = useState<T>(initialFilters);
  const [page, setPage] = useState<number>(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);

  // Selected items state
  const [selected, setSelected] = useState<string[]>([]);

  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(keyStorageFilters, JSON.stringify(filters));
  }, [filters, keyStorageFilters]);

  // Persist page to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(keyStoragePage, JSON.stringify(page));
  }, [page, keyStoragePage]);

  // Persist rowsPerPage to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(keyStorageRow, JSON.stringify(rowsPerPage));
  }, [rowsPerPage, keyStorageRow]);

  // Handlers
  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    []
  );

  const handleFilters = useCallback((name: string, value: any) => {
    setPage(0);
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleFilterSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleFilters('search', event.target.value);
    },
    [handleFilters]
  );

  const handleResetFilters = useCallback(() => {
    setPage(0);
    setFilters((prevState) => ({
      ...defaultFilters,
      search: (prevState as any).search || '',
    }));
  }, [defaultFilters]);

  const handleSelect = useCallback(
    (id: string) => {
      if (selected.includes(id)) {
        setSelected((prevState) => prevState.filter((item) => item !== id));
      } else {
        setSelected((prevState) => [...prevState, id]);
      }
    },
    [selected]
  );

  const handleSelectAll = useCallback((checked: boolean, items: string[]) => {
    if (checked) {
      setSelected(items);
    } else {
      setSelected([]);
    }
  }, []);

  return {
    filters,
    page,
    rowsPerPage,
    selected,
    openFilters,

    // Setters
    setFilters,
    setPage,
    setRowsPerPage,
    setSelected,

    // Handlers
    handleChangePage,
    handleChangeRowsPerPage,
    handleFilters,
    handleFilterSearch,
    handleResetFilters,
    handleSelect,
    handleSelectAll,

    // Storage keys
    keyStorageFilters,
    keyStoragePage,
    keyStorageRow,
  };
}
